"use client";

import { X } from "lucide-react";
import { useCart } from "@/packages/context/CartContext";
import { Variant } from "@/packages/types/products";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import ConfirmDialog from "./ConfirmDialog";
import { useWishlist } from "@/packages/context/wishlistContext";
import { useAuth } from "@/packages/context/AuthContext";
import { toast } from "sonner";
import { useState } from "react";

import EmptyCartView from "@/components/cart/EmptyCartView";
import CartItemRow from "@/components/cart/CartItemRow";
import SideCartSummary from "@/components/cart/SideCartSummary";
import ShippingTiersBox from "@/components/cart/ShippingTiersBox";




export default function SideCartV2({ onClose }: { onClose: () => void }) {

  const { cartItems, updateCart, removeCart, clearCart } = useCart();
  const { fastPush } = useFastRouter();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { isLoggedIn } = useAuth();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number | string; variant?: Variant } | null>(null);
  const [shippingFee, setShippingFee] = useState<number>(0);

  const updateQty = (
    id: number | string,
    quantity: number,
    variantId: number | undefined
  ) => {
    const item = cartItems.find(
      (i) => i.id === id && i.variant?.id === variantId
    );
    if (!item) return;
    updateCart(item.id, quantity, item.variant);
  };

  const removeItem = (id: number | string, variantId: number | undefined) => {
    const item = cartItems.find(
      (i) => i.id == id && i.variant?.id == variantId
    );
    if (!item) return;

    setItemToDelete({ id: item.id, variant: item.variant });
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      removeCart(itemToDelete.id, itemToDelete.variant);
    }
    setIsConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsConfirmOpen(false);
    setItemToDelete(null);
  };

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.quantity * (item.finalPrice ?? item.price),
    0
  );

  return (
    <div className="flex flex-col h-full w-full bg-white shadow-2xl p-5 md:p-6 text-slate-800 font-sans">
      {/* Header */}
      <div className="flex justify-between items-start pb-3 border-b border-slate-100 shrink-0">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Shopping Cart</h2>
          <p className="text-xs text-slate-500 font-medium">
            {cartItems.length} {cartItems.length === 1 ? "item" : "items"}
          </p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close cart"
          className="p-1 rounded-full text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors cursor-pointer"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Full Shipping Rates & Tiers Box */}
      {cartItems.length > 0 && (
        <ShippingTiersBox
          subtotal={subtotal}
          onShippingCalculated={(fee) => setShippingFee(fee)}
          className="my-3 shrink-0"
        />
      )}

      {/* Empty State */}
      {cartItems.length <= 0 && <EmptyCartView onClose={onClose} />}


      {/* Cart Items List */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-1 scrollbar-thin">
        {cartItems.map((item) => (
          <CartItemRow
            key={`${item.id}-${item.variant?.id ?? "no-variant"}`}
            item={item}
            onProductClick={(clickedItem) => {
              onClose();
              fastPush(`/products/${clickedItem.slug}`);
            }}
            onUpdateQty={updateQty}
            onRemoveItem={removeItem}
            onToggleWishlist={(targetItem) => {
              if (!isLoggedIn) {
                toast.info("Please sign in to manage wishlist");
                return;
              }
              toggleWishlist(targetItem);
            }}
            isInWishlist={isInWishlist(item.id)}
          />
        ))}

      </div>


      {/* Financial Summary & Actions */}
      {cartItems.length > 0 && (
        <SideCartSummary
          subtotal={subtotal}
          shippingFee={shippingFee}
          onCheckout={() => {
            onClose();
            fastPush("/checkout");
          }}
          onViewCart={() => {
            onClose();
            fastPush("/cart");
          }}
          onClearCart={() => clearCart()}
        />
      )}


      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Remove Item"
        message="Are you sure you want to remove this item from your cart?"
        confirmLabel="Yes, remove"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
